import { Components, Theme, createTheme } from "@mui/material";
import COLORS from "./colors";
import components from "./components";
import typography from "./typography";
import { getColorVariations, HEX_OPACITY } from "./utils";

const { MuiCssBaseline: _omitBaseline, ...baseComponents } = components as Components<Omit<Theme, "components">> & {
  MuiCssBaseline?: unknown;
};

/** Light theme for the Figma content panel (dark shell stays on themeDark). */
export const contentTheme = createTheme({
  components: {
    ...baseComponents,
    MuiLink: {
      styleOverrides: {
        root: {
          color: COLORS.primary,
          textDecoration: "none",
          ":visited": { color: "inherit" }
        }
      }
    },
    MuiButton: {
      ...baseComponents.MuiButton,
      styleOverrides: {
        ...(typeof baseComponents.MuiButton?.styleOverrides === "object"
          ? baseComponents.MuiButton.styleOverrides
          : {}),
        outlinedPrimary: {
          borderColor: COLORS.surface.border,
          color: COLORS.surface.primaryText,
          borderRadius: COLORS.radius.md,
          ":hover": {
            backgroundColor: COLORS.surface.tableHeader,
            borderColor: COLORS.surface.border,
            color: COLORS.surface.primaryText
          }
        }
      }
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: COLORS.surface.secondaryText,
          "&.Mui-focused": { color: COLORS.primary }
        },
        standard: {
          color: COLORS.surface.mutedText,
          "&.Mui-focused": { color: COLORS.surface.mutedText }
        }
      }
    },
    MuiInput: {
      styleOverrides: {
        root: {
          color: COLORS.surface.primaryText,
          "&:before": { borderColor: COLORS.surface.border },
          "&:hover:not(.Mui-disabled):before": { borderColor: COLORS.surface.secondaryText },
          "&:after": { borderColor: COLORS.primary }
        }
      }
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          color: COLORS.surface.primaryText,
          backgroundColor: COLORS.surface.card,
          "& .MuiOutlinedInput-notchedOutline": { borderColor: COLORS.surface.border },
          "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: COLORS.surface.secondaryText },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: COLORS.primary }
        }
      }
    },
    MuiSelect: {
      styleOverrides: {
        icon: { color: COLORS.surface.secondaryText }
      }
    },
    MuiTablePagination: {
      styleOverrides: {
        root: { color: COLORS.surface.secondaryText },
        selectIcon: { color: COLORS.surface.secondaryText }
      }
    },
    MuiIconButton: {
      styleOverrides: {
        root: { color: COLORS.surface.secondaryText }
      }
    },
    MuiTypography: {
      styleOverrides: {
        root: { color: "inherit" }
      }
    }
  },
  typography,
  palette: {
    mode: "light",
    background: {
      paper: COLORS.surface.card,
      default: COLORS.surface.page
    },
    text: {
      primary: COLORS.surface.primaryText,
      secondary: COLORS.surface.secondaryText,
      disabled: COLORS.surface.mutedText
    },
    primary: {
      ...getColorVariations(COLORS.primary),
      light: COLORS.primary + HEX_OPACITY.THREE,
      dark: COLORS.primary + HEX_OPACITY.NINE,
      contrastText: COLORS.white
    },
    secondary: {
      ...getColorVariations(COLORS.secondary),
      light: COLORS.secondary + HEX_OPACITY.THREE,
      dark: COLORS.secondary + HEX_OPACITY.NINE,
      contrastText: COLORS.white
    },
    info: {
      ...getColorVariations(COLORS.info),
      light: COLORS.info + HEX_OPACITY.THREE,
      dark: COLORS.info + HEX_OPACITY.NINE,
      contrastText: COLORS.white
    },
    success: {
      ...getColorVariations(COLORS.success),
      light: COLORS.success + HEX_OPACITY.THREE,
      dark: COLORS.success + HEX_OPACITY.NINE,
      contrastText: COLORS.white
    },
    warning: {
      ...getColorVariations(COLORS.warning),
      light: COLORS.warning + HEX_OPACITY.THREE,
      dark: COLORS.warning + HEX_OPACITY.NINE,
      contrastText: COLORS.white
    },
    error: {
      ...getColorVariations(COLORS.error),
      light: COLORS.error + HEX_OPACITY.THREE,
      dark: COLORS.error + HEX_OPACITY.NINE,
      contrastText: COLORS.white
    },
    common: {
      black: COLORS.black,
      white: COLORS.white
    },
    grey: {
      ...getColorVariations("#9CA4B3")
    },
    ...{
      custom: COLORS.custom
    }
  }
});

export default contentTheme;
